import {
  AggressorFlagEnum,
  MixpanelEventSource,
  PricingMode,
  UnifiedLiquidityEnum,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  type Security,
} from '@talos/kyoko';
import { useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useGetOrderPricingMode } from '../../../../../hooks/useGetOrderPricingMode';
import { useHandleClickMarketPrice } from '../../../../MarketDataCard/useHandleClickMarketPrice';
import type { MarketSelectorValue } from '../../../../MarketSelector/types';
import { useGetOrderCurrency } from '../../../../OMS/useGetOrderCurrency';
import { getMultilegLegMarketAccounts } from '../common/getMultilegLegMarketDetails';
import type {
  PriceGridCancelClickToTradePreviewHandler,
  PriceGridClickToTradePreviewOrder,
  PriceGridPrimeOrderClickHandler,
  PriceGridPrimingContext,
} from '../common/types';
import { getEffectivePriceIncrement, getPriceGridPrimedPrice } from './getPriceGridPrimedPrice';
import { getPrimeOrderMarketParams, type PrimeOrderMarketParams } from './getPrimeOrderMarketParams';

type PriceGridMarketPriceContext = {
  rowID: string;
  columnID?: string;
  security: Security;
  symbol: string;
  selectedMarketAccounts: string[];
  selectedMarkets: string[];
  selectedAggregation: string | undefined;
  useOrderMarketDefaults: boolean;
};

type PriceGridPrimeOrderMarketParams = PrimeOrderMarketParams & {
  useOrderMarketDefaults: boolean;
};

export function getPriceGridPrimeOrderSecurity({
  dataSecurity,
  securitiesBySymbol,
  symbol,
}: {
  dataSecurity: Security;
  securitiesBySymbol: ReadonlyMap<string, Security>;
  symbol: string;
}) {
  return securitiesBySymbol.get(symbol) ?? dataSecurity;
}

/**
 * Resolves the markets/market accounts to prime an order with for a clicked PriceGrid price cell.
 *
 * A column selection takes priority. An empty selector represents All Markets, so parent columns defer to the order
 * form's usual default aggregation or market accounts. Leg columns still fall back to their configured leg markets.
 */
export function getPriceGridPrimeOrderMarketParams({
  dataSecurity,
  legIndex,
  marketSelectorValue,
  marketsByName,
  marketAccountsByName,
}: {
  dataSecurity: Security;
  legIndex: number | undefined;
  marketSelectorValue: MarketSelectorValue | undefined;
  marketsByName: ReadonlyMap<string, unknown>;
  marketAccountsByName: ReadonlyMap<string, unknown>;
}): PriceGridPrimeOrderMarketParams {
  if (marketSelectorValue?.selections.length) {
    return {
      ...getPrimeOrderMarketParams({ marketSelectorValue, marketsByName, marketAccountsByName }),
      useOrderMarketDefaults: false,
    };
  }

  const legMarketAccounts = getMultilegLegMarketAccounts({ dataSecurity, legIndex });
  if (legMarketAccounts.length > 0) {
    return {
      selectedMarketAccounts: legMarketAccounts,
      selectedMarkets: [],
      selectedAggregation: undefined,
      useOrderMarketDefaults: false,
    };
  }

  return {
    selectedMarketAccounts: [],
    selectedMarkets: [],
    selectedAggregation: undefined,
    useOrderMarketDefaults: true,
  };
}

export function usePriceGridMarketPriceAction(
  primingContext?: PriceGridPrimingContext,
  requestedPriceIncrement?: string
) {
  const { getOrderCurrency } = useGetOrderCurrency();
  const { getOrderPricingMode } = useGetOrderPricingMode(undefined);
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const [marketPriceContext, setMarketPriceContext] = useState<PriceGridMarketPriceContext>();
  const {
    clickToTradePreviewOrder: marketDataCardPreviewOrder,
    maxOrderSizeDialogs,
    onCancelActive,
    onClickPrice,
  } = useHandleClickMarketPrice({
    symbol: marketPriceContext?.symbol,
    marketAccounts: marketPriceContext?.selectedMarketAccounts ?? [],
    markets: marketPriceContext?.selectedMarkets ?? [],
    aggregation: marketPriceContext?.selectedAggregation,
    useOrderMarketDefaults: marketPriceContext?.useOrderMarketDefaults ?? false,
    subAccount: primingContext?.subAccount,
    orderPresetID: primingContext?.orderPresetID,
    unifiedLiquidity: UnifiedLiquidityEnum.Disabled,
    defaultSubAccountId: undefined,
  });
  const onClickPriceRef = useRef(onClickPrice);
  onClickPriceRef.current = onClickPrice;

  const clickToTradePreviewOrder = useMemo((): PriceGridClickToTradePreviewOrder | undefined => {
    if (!marketDataCardPreviewOrder || !marketPriceContext) {
      return undefined;
    }

    return {
      ...marketDataCardPreviewOrder,
      rowID: marketPriceContext.rowID,
      columnID: marketPriceContext.columnID,
      security: marketPriceContext.security,
    };
  }, [marketDataCardPreviewOrder, marketPriceContext]);

  const onPrimeOrderClick = useCallbackRef<PriceGridPrimeOrderClickHandler>(
    ({
      data,
      symbol,
      columnID,
      intent = AggressorFlagEnum.Active,
      side,
      price,
      orderQty,
      marketSelectorValue,
      legIndex,
    }) => {
      if (!side || !price || !orderQty) {
        return;
      }

      if (
        clickToTradePreviewOrder &&
        (clickToTradePreviewOrder.rowID !== data.rowID ||
          clickToTradePreviewOrder.columnID !== columnID ||
          clickToTradePreviewOrder.side !== side)
      ) {
        flushSync(() => {
          onCancelActive();
        });
      }

      const { selectedMarketAccounts, selectedMarkets, selectedAggregation, useOrderMarketDefaults } =
        getPriceGridPrimeOrderMarketParams({
          dataSecurity: data.security,
          legIndex,
          marketSelectorValue,
          marketsByName,
          marketAccountsByName,
        });
      const security = getPriceGridPrimeOrderSecurity({
        dataSecurity: data.security,
        securitiesBySymbol,
        symbol,
      });
      const priceIncrement = getEffectivePriceIncrement({
        requestedPriceIncrement,
        securityMinPriceIncrement: security.MinPriceIncrement,
      });
      const primedPrice = getPriceGridPrimedPrice({
        isBPSPricing: getOrderPricingMode(security) === PricingMode.BPS,
        isBookMode: data.bucket.priceStepIndex != null,
        price,
        priceIncrement,
      });

      flushSync(() => {
        setMarketPriceContext({
          rowID: data.rowID,
          columnID,
          security,
          symbol,
          selectedMarketAccounts,
          selectedMarkets,
          selectedAggregation,
          useOrderMarketDefaults,
        });
      });

      const orderValues = {
        currency: getOrderCurrency(security, side),
        price: primedPrice,
        side,
        orderQty,
        intent,
      };

      onClickPriceRef.current({
        values: orderValues,
        source: MixpanelEventSource.PriceGrid,
        activateOrder: true,
      });
    }
  );

  const onCancelClickToTradePreview = useCallbackRef<PriceGridCancelClickToTradePreviewHandler>(({ rowID, side }) => {
    if (clickToTradePreviewOrder?.rowID === rowID && clickToTradePreviewOrder.side === side) {
      onCancelActive();
    }
  });

  return {
    clickToTradePreviewOrder,
    maxOrderSizeDialogs,
    onCancelClickToTradePreview,
    onPrimeOrderClick,
  };
}
